import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, ReactNode } from 'react';

import { cn } from '../../libs/utils';
import { semanticColor } from '../../theme/semanticColor';
import { IndeterminateProgress } from '../progress';

import { BaseButton, BaseButtonProps } from './BaseButton';

const FILLED_BUTTON_VARIANT = {
  'filled-primary':
    'bg-primary-background-main text-primary-foreground-inverse-main hover:bg-gradient-to-r from-neutral-background-hovered&pressed to-neutral-background-hovered&pressed',
  'filled-error':
    'bg-error-background-main text-error-foreground-inverse-main hover:bg-gradient-to-r from-neutral-background-hovered&pressed to-neutral-background-hovered&pressed'
};

const TONAL_BUTTON_VARIANT = {
  'tonal-secondary':
    'bg-secondary-background-low text-secondary-foreground-high hover:bg-gradient-to-r from-secondary-background-hovered&pressed to-secondary-background-hovered&pressed',
  'tonal-primary':
    'bg-primary-background-low text-primary-foreground-high hover:bg-gradient-to-r from-primary-background-hovered&pressed to-primary-background-hovered&pressed',
  'tonal-error':
    'bg-error-background-low text-error-foreground-high hover:bg-gradient-to-r from-error-background-hovered&pressed to-error-background-hovered&pressed'
};

const boxButtonVariants = cva('inline-flex items-center justify-center whitespace-nowrap transition-colors disabled:pointer-events-none', {
  variants: {
    variant: { ...FILLED_BUTTON_VARIANT, ...TONAL_BUTTON_VARIANT },
    size: {
      large: 'rounded-bzc-medium px-normal-xlarge py-normal-medium label-large-semibold',
      medium: 'rounded-bzc-small px-normal-large py-normal-small label-medium-semibold',
      small: 'py-tiny-large px-normal-medium rounded-bzc-small label-small-medium',
      xsmall: 'py-tiny-medium px-normal-small rounded-bzc-xsmall label-xsmall-medium'
    }
  },
  defaultVariants: {
    variant: 'filled-primary',
    size: 'medium'
  }
});

const indeterminateProgressVariants = cva('absolute left-0 right-0 flex items-center justify-center gap-tiny-large text-neutral-foreground-disabled', {
  variants: {
    size: {
      large: 'gap-tiny-large',
      medium: 'gap-tiny-large',
      small: 'gap-tiny-medium',
      xsmall: 'gap-tiny-medium'
    }
  },
  defaultVariants: {
    size: 'medium'
  }
});

const LOADING_COLOR = {
  'filled-primary': semanticColor.primary.foreground['inverse-main'],
  'filled-error': semanticColor.error.foreground['inverse-main'],
  'tonal-secondary': semanticColor.secondary.foreground.high,
  'tonal-primary': semanticColor.primary.foreground.high,
  'tonal-error': semanticColor.error.foreground.high
};

const LOADING_INDETERMINATE_PROGRESS_SIZE = {
  large: 20,
  medium: 18,
  small: 16,
  xsmall: 14
};

export interface BoxButtonProps extends Omit<BaseButtonProps, 'loadingColor'>, VariantProps<typeof boxButtonVariants> {
  loadingText?: ReactNode;
}

export const BoxButton = forwardRef<HTMLButtonElement, BoxButtonProps>(
  ({ className, variant = 'filled-primary', size, children, loadingText, ...props }, ref) => {
    const { disabled, isLoading } = props;

    return (
      <BaseButton
        className={cn(boxButtonVariants({ variant, size, className }), {
          'pointer-events-none bg-neutral-background-disabled-main text-neutral-foreground-disabled': disabled || isLoading
        })}
        loadingColor={LOADING_COLOR[variant as keyof typeof LOADING_COLOR]}
        ref={ref}
        {...props}
      >
        {isLoading && (
          <div className={indeterminateProgressVariants({ size })}>
            <IndeterminateProgress variant={'circular'} color={'dimmed'} size={size ? LOADING_INDETERMINATE_PROGRESS_SIZE[size] : 18} />
            {loadingText}
          </div>
        )}
        {children}
      </BaseButton>
    );
  }
);

BoxButton.displayName = 'BaseButton';
